// src/features/agent-builder/components/KnowledgeSourceManager.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { TextField, Button, Tooltip } from '@dopomogai/ui';
import { FileUploadField } from './fields/FileUploadField';
import { UploadableFile } from '@dopomogai/types';
import clsx from 'clsx'; // Added for potential future styling needs
// import { KnowledgeKeeperTeaser } from './KnowledgeKeeperTeaser';
// import { ContextWeaverNote } from './ContextWeaverNote';

interface KnowledgeSourceManagerProps {
  uploadedFiles: UploadableFile[];
  onFilesAdded: (newFiles: File[]) => void;
  onFileRemoved: (fileId: string) => void;
  onFileRetry?: (fileId: string) => void;
  websiteUrl: string;
  onWebsiteUrlChange: (url: string) => void;
  isScanningWebsite?: boolean;
  onScanWebsite: () => void;
}

export const KnowledgeSourceManager: React.FC<KnowledgeSourceManagerProps> = ({
  uploadedFiles,
  onFilesAdded,
  onFileRemoved,
  onFileRetry,
  websiteUrl,
  onWebsiteUrlChange,
  isScanningWebsite,
  onScanWebsite,
}) => {
  const { t } = useTranslation('agentBuilder');

  return (
    <div className="space-y-8">
      {/* File Upload Section - Relies on FileUploadField's internal Neobrutalist styling */}
      <section>
        <FileUploadField
          labelKey="agentBuilder:step2.knowledge.fileUpload.label"
          descriptionTextKey="agentBuilder:step2.knowledge.fileUpload.description"
          files={uploadedFiles}
          onFilesAdded={onFilesAdded}
          onFileRemoved={onFileRemoved}
          onFileRetry={onFileRetry}
        />
      </section>

      {/* Website Scraper Section */}
      <section>
        <h3 className="text-md font-semibold text-slate-700 mb-2">
          {t('step2.knowledge.website.subheading')}
        </h3>
        {/* Container for the website input and button */}
        <div
          className={clsx(
            "p-4 bg-slate-50 rounded-xl", // Changed to rounded-xl
            "border-2 border-black" // Neobrutalist border for this container
          )}
        >
          <div className="flex flex-col sm:flex-row items-end gap-3">
            <TextField
              labelKey="agentBuilder:step2.knowledge.website.label"
              placeholder={t('step2.knowledge.website.placeholder')}
              value={websiteUrl}
              onChange={(e) => onWebsiteUrlChange(e.target.value)}
              type="url"
              containerClassName="flex-grow"
              leadingIcon={<Globe size={18} className="text-slate-400" />}
              // TextField itself should have Neobrutalist styling from @dopomogai/ui
            />
            <Tooltip
              textKey="agentBuilder:step2.knowledge.website.scanTooltip"
            >
              <Button
                textKey="agentBuilder:step2.knowledge.website.scanButton"
                onClick={onScanWebsite}
                variant="secondary" // Button should have Neobrutalist styling from @dopomogai/ui
                disabled // Always disabled for MVP+ teaser
                icon={<Globe size={16} />}
                className="opacity-70 cursor-not-allowed w-full sm:w-auto" // Teaser styling
              />
            </Tooltip>
          </div>
          <p className="mt-3 text-xs text-slate-500"> {/* Adjusted margin for better spacing inside the box */}
            {t('step2.knowledge.website.description')}
          </p>
        </div>
      </section>

      {/* 
      <div className="mt-6">
        <KnowledgeKeeperTeaser /> // This would need its own Neobrutalist review
      </div>
      <div className="mt-6">
        <ContextWeaverNote /> // This would need its own Neobrutalist review
      </div> 
      */}
    </div>
  );
};